import _ from 'lodash'
import { useCallback, useEffect, useRef, useState } from 'react'
import { isSubmitSearchValid } from './entity-search-utils'
import { EntitySearchSubmitPayload, UI2EntitySearchFieldProps } from './ui2-entity-search-field-props'
import type { EditableFieldInternal } from '../core-field/editable-field-internal'

const SUBMIT_BUTTON_APPEAR_DELAY_MS = 400

interface UseEntitySearchSubmitModeArgs {
  minQueryLength: UI2EntitySearchFieldProps['minQueryLength']
  onSubmitSearch: UI2EntitySearchFieldProps['onSubmitSearch']
  onChange: EditableFieldInternal['onChange']
  rawQueryPath?: string
  frames?: any
}

interface UseEntitySearchSubmitModeResult {
  submitSearchFilter: string
  /** The filter value that was last explicitly submitted (committed). */
  submitRequestedSearchFilter: string
  /** True once the user has committed the current filter (button press / entity select / raw). */
  isCurrentFilterCommitted: boolean
  /** True while the results popover is open. */
  isSubmitResultsOpen: boolean
  /**
   * True after the submit button appearance delay fires, so the button can
   * animate in. Stays false until the user has typed enough characters.
   */
  isSubmitButtonVisible: boolean
  /** Count of sequential no-result submissions; reset to 0 on a result. */
  submitNoResultsCount: number
  handleSubmitFilterChange: (value: string) => void
  handleSubmitPress: () => boolean
  handleSubmitResultsClose: () => void
  handleSubmitSearch: (hasResults: boolean) => void
  handleCommitRawValue: (searchFilter: string) => void
  /** Called by the selection hook after an entity is picked from results. */
  handleEntityCommittedFromResults: () => void
  setIsSubmitResultsOpen: (open: boolean) => void
}

/**
 * All state for the submit-mode interaction: typed filter, button visibility timer,
 * committed flag, and results-open state. Platform components use this to keep
 * their submit-mode logic thin.
 */
export function useEntitySearchSubmitMode({
  minQueryLength,
  onSubmitSearch,
  onChange,
  rawQueryPath,
  frames,
}: UseEntitySearchSubmitModeArgs): UseEntitySearchSubmitModeResult {
  const [submitSearchFilter, setSubmitSearchFilter] = useState('')
  const [submitRequestedSearchFilter, setSubmitRequestedSearchFilter] = useState('')
  const [isCurrentFilterCommitted, setIsCurrentFilterCommitted] = useState(false)
  const [isSubmitResultsOpen, setIsSubmitResultsOpen] = useState(false)
  const [isSubmitButtonVisible, setIsSubmitButtonVisible] = useState(false)
  const [submitNoResultsCount, setSubmitNoResultsCount] = useState(0)

  const buttonShowTimer = useRef<ReturnType<typeof setTimeout> | null>(null)

  // Drive button visibility: clear the timer when there's nothing to submit;
  // set a delay timer when the filter changes to a valid length.
  useEffect(() => {
    const hasTyped = !_.isEmpty(_.trim(submitSearchFilter)) && !isCurrentFilterCommitted
    const valid = isSubmitSearchValid(submitSearchFilter, minQueryLength)

    if (!hasTyped || !valid) {
      if (buttonShowTimer.current) {
        clearTimeout(buttonShowTimer.current)
        buttonShowTimer.current = null
      }
      if (isSubmitButtonVisible) setIsSubmitButtonVisible(false)
      return
    }

    if (buttonShowTimer.current) clearTimeout(buttonShowTimer.current)
    buttonShowTimer.current = setTimeout(() => {
      buttonShowTimer.current = null
      setIsSubmitButtonVisible(true)
    }, SUBMIT_BUTTON_APPEAR_DELAY_MS)

    return () => {
      if (buttonShowTimer.current) {
        clearTimeout(buttonShowTimer.current)
        buttonShowTimer.current = null
      }
    }
    // Only re-run when filter text or committed state changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [submitSearchFilter, isCurrentFilterCommitted])

  const handleSubmitFilterChange = useCallback(
    (value: string) => {
      if (rawQueryPath && !_.isEmpty(frames?.getValue?.(rawQueryPath))) {
        ;(onChange as any)?.(null, false, rawQueryPath)
      }
      setSubmitSearchFilter(value)
      setIsCurrentFilterCommitted(false)
      setIsSubmitResultsOpen(false)
    },
    [rawQueryPath, frames, onChange],
  )

  // Returns true when the submit is valid and the state was updated.
  const handleSubmitPress = useCallback((): boolean => {
    const trimmed = _.trim(submitSearchFilter)
    if (!isSubmitSearchValid(trimmed, minQueryLength)) return false
    setSubmitRequestedSearchFilter(trimmed)
    setIsSubmitResultsOpen(true)
    return true
  }, [submitSearchFilter, minQueryLength])

  const handleSubmitResultsClose = useCallback(() => {
    setIsSubmitResultsOpen(false)
  }, [])

  const handleSubmitSearch = useCallback(
    (hasResults: boolean) => {
      setSubmitNoResultsCount((prev) => {
        const count = hasResults ? 0 : prev + 1
        onSubmitSearch?.({ hasResults, numberOfAttempts: count } as EntitySearchSubmitPayload)
        return count
      })
    },
    [onSubmitSearch],
  )

  const handleCommitRawValue = useCallback(
    (searchFilter: string) => {
      if (rawQueryPath) {
        ;(onChange as any)?.(searchFilter || null, false, rawQueryPath)
      }
      setIsCurrentFilterCommitted(true)
      setIsSubmitResultsOpen(false)
    },
    [rawQueryPath, onChange],
  )

  const handleEntityCommittedFromResults = useCallback(() => {
    setIsCurrentFilterCommitted(true)
    setIsSubmitResultsOpen(false)
  }, [])

  return {
    submitSearchFilter,
    submitRequestedSearchFilter,
    isCurrentFilterCommitted,
    isSubmitResultsOpen,
    isSubmitButtonVisible,
    submitNoResultsCount,
    handleSubmitFilterChange,
    handleSubmitPress,
    handleSubmitResultsClose,
    handleSubmitSearch,
    handleCommitRawValue,
    handleEntityCommittedFromResults,
    setIsSubmitResultsOpen,
  }
}
